import { z } from 'zod';
import { SLOT_MINUTES_OPTIONS } from './constants.js';

export const koreanPhoneSchema = z
  .string()
  .regex(/^01[016789]-?\d{3,4}-?\d{4}$/, '올바른 휴대전화번호를 입력해주세요')
  .transform((v) => v.replace(/-/g, ''));

export const slugSchema = z
  .string()
  .min(3, '슬러그는 3자 이상이어야 합니다')
  .max(40, '슬러그는 40자 이내여야 합니다')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, '영문 소문자, 숫자, 하이픈만 허용됩니다');

export const genderSchema = z.enum(['male', 'female']);

export const bookingCustomerSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요').max(40),
  birth_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '생년월일 형식 오류'),
  phone: koreanPhoneSchema,
  gender: genderSchema,
  privacy_consent: z.literal(true, {
    errorMap: () => ({ message: '개인정보 수집·이용에 동의해주세요' }),
  }),
});

export type BookingCustomer = z.infer<typeof bookingCustomerSchema>;

export const paymentMethodSchema = z.enum(['toss_pay', 'virtual_account']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const createBookingInputSchema = z.object({
  shaman_slug: slugSchema,
  starts_at: z.string().datetime(),
  payment_method: paymentMethodSchema,
  customer: bookingCustomerSchema,
});
export type CreateBookingInput = z.infer<typeof createBookingInputSchema>;

export const signupShamanSchema = z.object({
  email: z.string().email('이메일 형식이 올바르지 않습니다'),
  password: z.string().min(8, '비밀번호는 8자 이상이어야 합니다').max(72),
  display_name_ko: z.string().min(1).max(40),
  slug: slugSchema,
  phone: koreanPhoneSchema,
});
export type SignupShamanInput = z.infer<typeof signupShamanSchema>;

export const availabilityRuleSchema = z.object({
  weekday: z.number().int().min(0).max(6),
  start_time: z.string().regex(/^([0-1]\d|2[0-3]):[0-5]\d$/),
  end_time: z.string().regex(/^([0-1]\d|2[0-3]):[0-5]\d$/),
  slot_minutes: z.union(
    SLOT_MINUTES_OPTIONS.map((n) => z.literal(n)) as [
      z.ZodLiteral<30>,
      z.ZodLiteral<45>,
      z.ZodLiteral<60>,
      z.ZodLiteral<90>,
      z.ZodLiteral<120>,
    ],
  ),
  price_krw: z.number().int().min(0),
  effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  effective_until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type AvailabilityRuleInput = z.infer<typeof availabilityRuleSchema>;

export const cancelBookingSchema = z.object({
  booking_id: z.string().uuid(),
  cancel_token: z.string().uuid(),
  refund_account: z
    .object({
      bank: z.string().min(1),
      account_number: z.string().min(1),
      holder_name: z.string().min(1),
    })
    .optional(),
});
export type CancelBookingInput = z.infer<typeof cancelBookingSchema>;
